import { KeyboardEventHandler, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { TBenefitsSavings } from '@/common/integrations/menhir/employee/savings/savings.types';
import { benefitOptionsMapper } from '@/common/utils/benefits/benefit-utils';
import { BenefitIcon } from '@/web/components/BenefitIcon/BenefitIcon';
import { Button, IconChevronDown } from '@coverflex-tech/hypernova';
import { MoneyUtils } from '@coverflex-tech/hypernova-utils';
import classNames from 'classnames';
import css from './accordion.module.css';

interface IProps {
  savings: TBenefitsSavings[];
  openLearningCentre: () => void;
  pristine: boolean;
  setPristine: (value: boolean) => void;
}

// If this gets reused we should make it generic and animate the height dynamically
// Find a11y guidelines here: https://www.w3.org/TR/wai-aria-practices-1.2/#accordion
const Accordion = ({
  savings,
  openLearningCentre,
  pristine,
  setPristine,
}: IProps) => {
  const { t } = useTranslation();
  const [openedItem, setOpenedItem] = useState<number | undefined>(0);
  const refList = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    refList.current = refList.current.slice(0, savings.length);
  }, [savings]);

  const focusItem = (index: number) => {
    refList.current[index]?.focus();
  };

  return (
    <div
      onKeyDown={({ key }) => {
        if (key === 'Tab') setPristine(false);
        if (key === 'Escape') setPristine(true);
      }}
      className={css.items}
    >
      {savings.map(
        (
          {
            slug,
            totalSavings,
            details: { totalGross, totalSpent, socialSecurity, taxIncome },
          },
          index,
          array
        ) => {
          if (!benefitOptionsMapper[slug]) {
            return null;
          }
          const { label } = benefitOptionsMapper[slug];
          const expanded = openedItem === index;
          const sectionId = `${label}-section`;
          const triggerId = `${label}-trigger`;

          const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = (e) => {
            const { key } = e;
            switch (key) {
              case ' ':
              case 'Tab':
                setPristine(false);
                break;
              case 'Enter':
                setPristine(false);
                e.preventDefault(); // prevent otherwise it does not execute this code
                if (expanded) {
                  setOpenedItem(undefined);
                } else {
                  setOpenedItem(index);
                }
                break;
              case 'ArrowDown':
                setPristine(false);
                focusItem(index >= array.length - 1 ? 0 : index + 1);
                break;
              case 'ArrowUp':
                setPristine(false);
                focusItem(index === 0 ? array.length - 1 : index - 1);
                break;
              case 'Home':
                setPristine(false);
                focusItem(0);
                break;
              case 'End':
                setPristine(false);
                focusItem(array.length - 1);
                break;
              default:
            }
          };

          return (
            <div
              className={classNames(css.item, {
                [css.disabled!]: !expanded,
              })}
              key={`${label}-key`}
            >
              <h3 /* Kind of weird but using a header is part of the ARIA guidelines */
                className={css.header}
              >
                <button
                  ref={(el) => {
                    refList.current[index] = el;
                  }}
                  id={triggerId}
                  aria-expanded={expanded}
                  aria-controls={sectionId}
                  onClick={() => {
                    AnalyticsService.trackCustomEvent(
                      'expand_savings_category_button_clicked',
                      { benefit_category: slug }
                    );
                    if (expanded) {
                      setOpenedItem(undefined);
                    } else {
                      setOpenedItem(index);
                    }
                  }}
                  onKeyDown={onKeyDown}
                  className={classNames(css.itemExpand, {
                    [css.pristine!]: pristine,
                  })}
                >
                  <div className={css.itemName}>
                    <BenefitIcon benefit={slug} className={css.benefitIcon} />
                    <div>{t(label)}</div>
                  </div>
                  <div className={css.benefitSavings}>
                    <div className={css.savingsValue}>
                      {MoneyUtils.moneyToString(totalSavings)}
                    </div>
                    <IconChevronDown
                      className={classNames(css.chevron, {
                        [css.flipped!]: expanded,
                      })}
                    />
                  </div>
                </button>
              </h3>

              <div
                id={sectionId}
                role="region"
                aria-labelledby={triggerId}
                className={classNames(css.itemContentContainer, {
                  [css.expanded!]: expanded,
                  [css.pristine!]: pristine,
                })}
              >
                <div className={css.accordionItemContent}>
                  <div className={css.detailRow}>
                    <div className={css.rowLabel}>
                      {t('dashboard.savings.savings_per_benefit.total')}
                    </div>
                    <div className={css.column}>
                      {MoneyUtils.moneyToString(totalSpent)}
                    </div>
                  </div>
                  <div className={css.detailRow}>
                    <div className={css.rowLabel}>
                      {t('dashboard.savings.savings_per_benefit.gross')}
                    </div>
                    <div className={css.column}>
                      {MoneyUtils.moneyToString(totalGross)}
                    </div>
                  </div>
                  {!!socialSecurity && (
                    <div className={css.detailRow}>
                      <div className={css.rowLabel}>
                        {t(
                          'dashboard.savings.savings_per_benefit.social_security',
                          { percent: (socialSecurity.rate * 100).toFixed(2) }
                        )}
                      </div>
                      <div>
                        {MoneyUtils.moneyToString(socialSecurity.amount)}
                      </div>
                    </div>
                  )}
                  {!!taxIncome && (
                    <div
                      className={classNames(css.detailRow, css.rowExtraSpace)}
                    >
                      <div className={css.rowLabel}>
                        {t('dashboard.savings.savings_per_benefit.irs', {
                          percent: (taxIncome.rate * 100).toFixed(2),
                        })}
                      </div>
                      <div className={css.column}>
                        {MoneyUtils.moneyToString(taxIncome.amount)}
                      </div>
                    </div>
                  )}
                  {expanded /* This is so the button doesn't capture focus unless its expanded */ && (
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={() => {
                        AnalyticsService.trackCustomEvent(
                          'learning_centre_savings_button_clicked',
                          { benefit_category: slug }
                        );
                        openLearningCentre();
                      }}
                      className={classNames(css.link, {
                        [css.pristine!]: pristine,
                      })}
                    >
                      {t('dashboard.savings.savings_per_benefit.how')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        }
      )}
    </div>
  );
};

export default Accordion;
